import {
  closestCorners,
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import axios from "axios";
import ActionButton from "components/common/ActionButton";
import LightBoxLatestExample from "components/common/LightBoxLatestExample";
import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  FloatingLabel,
  Form,
  Row,
  Image,
  Spinner,
  Table,
} from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { useFieldArray, useFormContext } from "react-hook-form";
import { FaImages } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BASEURL } from "utils/customConfig";
import ImageCropperModalV1 from "components/image_crop/ImageCropperModalV1";

const SortableItem = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr ref={setNodeRef} style={style}>
      <td
        {...listeners}
        {...attributes}
        style={{
          cursor: "grab",
          textAlign: "center",
          width: "10px",
        }}
      >
        ☰
      </td>
      {children}
    </tr>
  );
};

const getVideoEmbed = (url) => {
  if (!url) return null;

  // YouTube
  const ytMatch =
    url.match(/youtube\.com\/watch\?v=([^&]+)/) ||
    url.match(/youtu\.be\/([^?&]+)/);
  if (ytMatch) {
    return {
      type: "iframe",
      src: `https://www.youtube.com/embed/${ytMatch[1]}`,
    };
  }

  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return {
      type: "iframe",
      src: `https://player.vimeo.com/video/${vimeoMatch[1]}`,
    };
  }

  // Dailymotion
  const dmMatch =
    url.match(/dailymotion\.com\/video\/([^_]+)/) ||
    url.match(/dai\.ly\/([^?&]+)/);
  if (dmMatch) {
    return {
      type: "iframe",
      src: `https://www.dailymotion.com/embed/video/${dmMatch[1]}`,
    };
  }

  // Facebook
  if (url.includes("facebook.com")) {
    return {
      type: "iframe",
      src: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(
        url,
      )}&show_text=false`,
    };
  }

  // MP4 / WebM direct video
  if (url.match(/\.(mp4|webm)$/)) {
    return {
      type: "video",
      src: url,
    };
  }

  return null;
};

const MediaInfoV1 = () => {
  const {
    register,
    getValues,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    fields: mediaInfos,
    append: appendMedia,
    remove: removeMedia,
    move,
  } = useFieldArray({
    control,
    name: "mediaInfos",
  });

  const [previews, setPreviews] = useState({});
  const [imagePreviews, setImagePreviews] = useState([]);

  const [imagesList, setImagesList] = useState({});
  const [imagesPreviewList, setImagesPreviewList] = useState({});
  const [serverImagesList, setServerImagesList] = useState({});

  const [showCropModal, setShowCropModal] = useState(false);
  const [tempFiles, setTempFiles] = useState([]);
  const [tempPreview, setTempPreview] = useState("");

  // const Dropzone = ({ onDrop, index }) => {
  //   const { getRootProps, getInputProps } = useDropzone({
  //     accept: {
  //       "image/jpeg": [".jpeg", ".jpg"],
  //       "image/png": [".png"],
  //     },
  //     multiple: true,
  //     onDrop: (acceptedFiles) => onDrop(acceptedFiles, index),
  //   });

  //   return (
  //     <div {...getRootProps({ className: "dropzone" })}>
  //       <input {...getInputProps()} />
  //       <div style={{ padding: "5px" }}>
  //         <FaImages size={25} />
  //       </div>
  //     </div>
  //   );
  // };

  const PRODUCT_IMAGE_SIZE = window.imageConfig?.PRODUCT_IMAGE_SIZE || {
    width: 600,
    height: 600,
  };

  const Dropzone = ({ index }) => {
    const { getRootProps, getInputProps } = useDropzone({
      accept: {
        "image/jpeg": [".jpeg", ".jpg"],
        "image/png": [".png"],
      },
      multiple: false,

      onDrop: (acceptedFiles) => {
        const file = acceptedFiles[0];
        if (!file) return;

        // store temp file for cropping
        setTempFiles([file]);
        setTempPreview(URL.createObjectURL(file));
        setShowCropModal(true);
      },
    });

    return (
      <div {...getRootProps({ className: "dropzone" })}>
        <input {...getInputProps()} />
        <div style={{ padding: "5px" }}>
          <FaImages size={25} />
          <span
            style={{
              fontSize: "10px",
              color: "red",
              marginLeft: 4,
              marginTop: 8,
            }}
          >
            ({PRODUCT_IMAGE_SIZE.width}px × {PRODUCT_IMAGE_SIZE.height}px)
          </span>
        </div>
      </div>
    );
  };

  const handleCropResult = async (croppedFile) => {
    if (!croppedFile) return;

    setShowCropModal(false);

    try {
      // upload cropped image
      const response = await uploadImages([croppedFile]);

      const destinations = response
        ?.map((item) => item.destination)
        .filter(Boolean);

      const currentLength = mediaInfos.length;

      destinations.forEach((destination, i) => {
        appendMedia({
          mediaInfo: destination,
          contentType: "image",
          status: true,
          sortOrder: currentLength + i + 1,
        });
      });

      setTempFiles([]);
      setTempPreview("");
    } catch (err) {
      console.error(err);
      toast.error("Image upload failed");
    }
  };

  const uploadImages = async (files) => {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append("files", file); // 'files[]' is the key name on the backend
    });

    try {
      const response = await axios.post("/Catalogue/ProductFiles", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Assuming your backend returns a list of URLs
      return response?.data;
    } catch (error) {
      console.error("Error uploading images:", error);
      return [];
    }
  };

  const handleDrop = useCallback(
    async (acceptedFiles, index) => {
      // Create object URLs for preview
      const newImages = acceptedFiles.map((file) => URL.createObjectURL(file));

      // Update image preview list
      setImagesPreviewList((prevImagesList) => {
        const updatedImagesPreview = {
          ...prevImagesList,
          [index]: [...(prevImagesList[index] || []), ...newImages],
        };

        return updatedImagesPreview;
      });

      // Upload images and get the URLs
      const response = await uploadImages(acceptedFiles);

      // Filter out null values from response
      const destinations = response
        ?.map((item) => item.destination)
        .filter((url) => url !== null);

      // Get the current length of mediaInfos to determine the starting sort order
      const currentMediaInfosLength = mediaInfos.length;

      // Create a new mediaInfo object for each destination and append it to mediaInfos
      destinations.forEach((destination, index) => {
        appendMedia({
          mediaInfo: destination,
          contentType: "image",
          status: true,
          sortOrder: currentMediaInfosLength + index + 1, // Set sort order
        });
      });
    },
    [uploadImages, appendMedia, mediaInfos],
  );

  const normalizeImageUrl = (path) => {
    if (!path) return null;
    return `${BASEURL}/wwwroot/${path.replace(/\\/g, "/")}`;
  };

  const removeImage = (rowIndex, imageIndex) => {
    console.log(rowIndex, imageIndex);
    setImagesPreviewList((prevImagesList) => {
      // Ensure the rowIndex exists and is valid
      if (!prevImagesList[rowIndex]) {
        console.warn(`Row index ${rowIndex} not found.`);
        return prevImagesList;
      }

      // Filter out the image at imageIndex
      const updatedImages = prevImagesList[rowIndex].filter(
        (_, index) => index !== imageIndex,
      );

      // Update the form value
      // setValue(`mediaInfos[${rowIndex}].imagesPreview`, updatedImages);

      // Return the updated state
      return {
        ...prevImagesList,
        [rowIndex]: updatedImages,
      };
    });
  };

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
  );

  const updateSortOrder = () => {
    // Get the current value of mediaInfos (make sure it's up to date)
    const updatedMediaInfos = getValues("mediaInfos");

    // Iterate over the array and set the sortOrder for each item
    updatedMediaInfos.forEach((_, index) => {
      setValue(`mediaInfos[${index}].sortOrder`, index);
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = mediaInfos.findIndex((item) => item.id === active.id);
      const newIndex = mediaInfos.findIndex((item) => item.id === over.id);
      move(oldIndex, newIndex);

      updateSortOrder();
    }
  };

  const getContentType = (url) => {
    if (url.match(/\.(jpeg|jpg|png)$/)) return "image";
    if (
      url.match(/\.(mp4|webm)$/) ||
      url.includes("youtube.com") ||
      url.includes("youtu.be") ||
      url.includes("vimeo.com") ||
      url.includes("dailymotion.com") ||
      url.includes("dai.ly") ||
      url.includes("facebook.com")
    )
      return "video";
    return "unknown";
  };

  const handleAppend = () => {
    appendMedia({ contentType: "video", status: true });

    // Delay the sortOrder update to ensure the new item is appended first
    setTimeout(() => {
      updateSortOrder();
    }, 0);
  };

  //   const handleUrlChange = (index) => {
  //     const url = getValues(`mediaInfos[${index}].mediaInfo`);
  //     if (url) {
  //       const type = getContentType(url);
  //       setValue(`mediaInfos[${index}].contentType`, type);
  //       setPreviews((prev) => ({ ...prev, [index]: { type, url } }));
  //     } else {
  //       // Remove preview if URL is cleared
  //       setPreviews((prev) => {
  //         const updatedPreviews = { ...prev };
  //         delete updatedPreviews[index];
  //         return updatedPreviews;
  //       });
  //     }
  //   };

  const handleUrlChange = (index) => {
    const id = mediaInfos[index].id; // use id
    const url = getValues(`mediaInfos[${index}].mediaInfo`);

    if (url) {
      const type = getContentType(url);
      setValue(`mediaInfos[${index}].contentType`, type);
      setPreviews((prev) => ({ ...prev, [id]: { type, url } })); // store by id
    } else {
      setPreviews((prev) => {
        const updatedPreviews = { ...prev };
        delete updatedPreviews[id];
        return updatedPreviews;
      });
    }
  };

  useEffect(() => {
    mediaInfos.forEach((_, index) => handleUrlChange(index));
  }, [mediaInfos]);

  return (
    <>
      <Row className="gx-2 gy-2">
        <Card.Header as="h6" className="bg-body-tertiary">
          Images
        </Card.Header>
        <Col md="12">
          <Dropzone onDrop={handleDrop} />
        </Col>

        <Col md="12">
          <Col md="12">
            <MyImageGallery
              mediaInfos={mediaInfos
                .map((item, index) => ({ ...item, index })) // attach original index
                .filter((item) => item.contentType === "image")}
              imagesPreviewList={imagesPreviewList}
              removeMedia={removeMedia}
            />
          </Col>
        </Col>
        <Card.Header as="h6" className="bg-body-tertiary">
          Videos
        </Card.Header>
        <Col md="12">
          <Row className="gx-2 gy-1">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={mediaInfos.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <Table responsive>
                  <thead className="bg-200">
                    <tr>
                      <th
                        scope="col"
                        className="w-0 fs-10 text-center"
                        style={{ width: "30px" }}
                      >
                        Order
                      </th>
                      <th scope="col" className="fs-10">
                        Link
                      </th>
                      <th
                        scope="col"
                        className="fs-10 text-center"
                        style={{ width: "30px" }}
                      >
                        Preview
                      </th>
                      <th
                        scope="col"
                        style={{ padding: "9px", width: "30px" }}
                        className="text-end"
                      >
                        <ActionButton
                          icon="plus"
                          variant="success"
                          title="Add"
                          className="px-1 py-0"
                          onClick={handleAppend}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {mediaInfos.map(
                      (item, index) =>
                        item.contentType === "video" && (
                          <SortableItem key={item.id} id={item.id}>
                            <td style={{ padding: "5px" }}>
                              <Form.Group controlId={`formGroupName-${index}`}>
                                <Form.Control
                                  type="text"
                                  placeholder="Link"
                                  // isInvalid={
                                  //   !!errors.mediaInfos?.[index]?.mediaInfo
                                  // }
                                  {...register(
                                    `mediaInfos[${index}].mediaInfo`,
                                    {
                                      onChange: () => handleUrlChange(index),
                                    },
                                  )}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {/* {
                                    errors.mediaInfos?.[index]?.mediaInfo
                                      ?.message
                                  } */}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </td>
                            {/* <td
                              style={{ padding: "5px" }}
                              className="text-center"
                            >
                              {previews[index]?.type === "video" && (
                                <video width="200" height="auto" controls>
                                  <source
                                    src={previews[index]?.url}
                                    type="video/mp4"
                                  />
                                  Your browser does not support the video tag.
                                </video>
                              )}
                            </td> */}
                            <td
                              style={{ padding: "5px" }}
                              className="text-center"
                            >
                              {(() => {
                                const embed = getVideoEmbed(
                                  previews[item.id]?.url,
                                ); // use item.id
                                if (!embed) return null;

                                if (embed.type === "iframe") {
                                  return (
                                    <iframe
                                      src={embed.src}
                                      width="200"
                                      height="120"
                                      style={{
                                        border: "none",
                                        borderRadius: 8,
                                      }}
                                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                      allowFullScreen
                                    />
                                  );
                                }

                                if (embed.type === "video") {
                                  return (
                                    <video
                                      width="200"
                                      height="120"
                                      controls
                                      muted
                                      playsInline
                                      preload="metadata"
                                      style={{
                                        borderRadius: 8,
                                        background: "#000",
                                      }}
                                    >
                                      <source
                                        src={embed.src}
                                        type="video/mp4"
                                      />
                                      Your browser does not support the video
                                      tag.
                                    </video>
                                  );
                                }

                                return null;
                              })()}
                            </td>

                            <td
                              style={{ padding: "5px" }}
                              className="text-center"
                            >
                              <ActionButton
                                icon="trash-alt"
                                title="Delete"
                                variant="danger"
                                className="px-1 py-0"
                                onClick={() => removeMedia(index)}
                              />
                            </td>
                          </SortableItem>
                        ),
                    )}
                  </tbody>
                </Table>
              </SortableContext>
            </DndContext>
          </Row>
        </Col>
      </Row>

      <ImageCropperModalV1
        show={showCropModal}
        imageUrl={tempPreview}
        type="product-image"
        onClose={() => setShowCropModal(false)}
        onCropped={handleCropResult}
      />
    </>
  );
};

const MyImageGallery = ({ mediaInfos, imagesPreviewList, removeMedia }) => {
  const normalizeImageUrl = (path) => {
    if (!path) return null;
    const fixed = path.replace(/\\/g, "/");
    return `${BASEURL}/wwwroot/${fixed}`;
  };

  // Map images to objects containing the original mediaInfos index
  const combinedImages = mediaInfos.map((item) => {
    const url =
      imagesPreviewList[item.index]?.[0] || // preview if exists
      normalizeImageUrl(item.mediaInfo); // else server
    return { url, index: item.index };
  });

  return (
    <LightBoxLatestExample images={combinedImages.map((img) => img.url)}>
      {(setImgIndex) => (
        <Row className="g-2">
          {combinedImages.map((img) => (
            <Col
              xs={6}
              md={3}
              lg={1}
              key={img.index}
              className="position-relative"
            >
              <Image
                src={img.url}
                fluid
                rounded
                className="cursor-pointer"
                onClick={() => setImgIndex(img.index)}
              />
              <Button
                variant="danger"
                size="sm"
                onClick={() => removeMedia(img.index)} // original index used here
                className="position-absolute top-0 end-0"
                style={{ padding: "0px 4px" }}
              >
                &times;
              </Button>
            </Col>
          ))}
        </Row>
      )}
    </LightBoxLatestExample>
  );
};

export default MediaInfoV1;
